import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import { Plus, Pencil, Trash2, Lock, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

export const ManageExpenseCategoriesDialog = ({ open, onOpenChange, categories, onCategoriesChanged }) => {
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!newName.trim()) { toast.error('Category name is required'); return; }
    setSaving(true);
    try {
      await api.post('/tournaments/config/expense-categories', { name: newName.trim(), description: newDescription.trim() || null });
      toast.success(`Category "${newName.trim()}" created`);
      setNewName(''); setNewDescription('');
      onCategoriesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create category'); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) { toast.error('Category name is required'); return; }
    setSaving(true);
    try {
      await api.put(`/tournaments/config/expense-categories/${id}`, { name: editName.trim(), description: editDescription.trim() || null });
      toast.success('Category updated');
      setEditingId(null);
      onCategoriesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (cat) => {
    try {
      await api.put(`/tournaments/config/expense-categories/${cat.id}`, { is_active: !cat.is_active });
      toast.success(cat.is_active ? 'Category deactivated' : 'Category activated');
      onCategoriesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (cat) => {
    try {
      await api.delete(`/tournaments/config/expense-categories/${cat.id}`);
      toast.success(`Category "${cat.name}" deleted`);
      onCategoriesChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const startEdit = (cat) => { setEditingId(cat.id); setEditName(cat.name); setEditDescription(cat.description || ''); };

  const systemCategories = categories.filter(c => c.is_system);
  const customCategories = categories.filter(c => !c.is_system);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="manage-expense-categories-dialog">
        <DialogHeader>
          <DialogTitle>Manage Expense Categories</DialogTitle>
          <DialogDescription>Create custom categories for recording tournament expenses</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Create new category */}
          <div className="p-4 border-2 border-dashed border-slate-200 rounded-lg space-y-3">
            <Label className="font-medium">Add New Category</Label>
            <Input placeholder="Category name (e.g. Equipment Rental)" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-category-name" />
            <Input placeholder="Description (optional)" value={newDescription} onChange={(e) => setNewDescription(e.target.value)} data-testid="new-category-description" />
            <Button size="sm" onClick={handleCreate} disabled={saving || !newName.trim()} data-testid="create-category-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Add Category
            </Button>
          </div>

          {/* Custom categories */}
          {customCategories.length > 0 && (
            <div className="space-y-2">
              <Label className="font-medium text-slate-700">Custom Categories</Label>
              {customCategories.map(cat => (
                <div key={cat.id} className={`flex items-center gap-3 p-3 rounded-lg border ${cat.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}>
                  {editingId === cat.id ? (
                    <div className="flex-1 space-y-2">
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`edit-name-${cat.id}`} />
                      <Input value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Description" />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleUpdate(cat.id)} disabled={saving}>Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{cat.name}</p>
                        {cat.description && <p className="text-xs text-slate-500">{cat.description}</p>}
                        <p className="text-xs text-slate-400 mt-0.5">Code: {cat.code}</p>
                      </div>
                      <Switch checked={cat.is_active} onCheckedChange={() => handleToggleActive(cat)} data-testid={`toggle-active-${cat.id}`} />
                      <Button size="sm" variant="ghost" onClick={() => startEdit(cat)} data-testid={`edit-category-${cat.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(cat)} data-testid={`delete-category-${cat.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* System categories (read-only) */}
          <div className="space-y-2">
            <Label className="font-medium text-slate-500">Built-in Categories</Label>
            {systemCategories.map(cat => (
              <div key={cat.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                <div className="flex-1">
                  <p className="font-medium text-sm text-slate-700">{cat.name}</p>
                  {cat.description && <p className="text-xs text-slate-400">{cat.description}</p>}
                </div>
                <Badge variant="outline" className="text-xs"><Lock className="h-3 w-3 mr-1" />System</Badge>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
